import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { addOrder, getUserOrders } from '../firebase/services';
import { notifyNewOrder } from '../services/telegramService';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) setCart(JSON.parse(savedCart));
  }, []);

  const addToCart = (product) => {
    const existing = cart.find(item => 
      item.id === product.id && 
      JSON.stringify(item.variant) === JSON.stringify(product.variant)
    );
    
    let newCart;
    if (existing) {
      newCart = cart.map(item =>
        item.id === product.id && 
        JSON.stringify(item.variant) === JSON.stringify(product.variant)
          ? { ...item, quantity: item.quantity + 1 } 
          : item
      );
    } else {
      newCart = [...cart, { 
        ...product, 
        quantity: 1,
        cartId: Date.now() + Math.random() // Unique cart ID for variants
      }];
    }
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const removeFromCart = (cartId) => {
    const newCart = cart.filter(item => item.cartId !== cartId);
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const updateQuantity = (cartId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartId);
      return;
    }
    const newCart = cart.map(item =>
      item.cartId === cartId ? { ...item, quantity } : item
    );
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.setItem('cart', JSON.stringify([]));
  };

  const placeOrder = async (userEmail, orderData = null) => {
    try {
      const finalOrderData = orderData || {
        items: cart,
        total: cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
        userEmail,
        status: 'pending'
      };
      
      console.log('Placing order:', finalOrderData);
      const orderId = await addOrder(finalOrderData);
      console.log('Order placed with ID:', orderId);
      
      // Send Telegram notification
      try {
        await notifyNewOrder(finalOrderData, orderId);
      } catch (error) {
        console.error('Failed to send Telegram notification:', error);
        // Don't fail the order if notification fails
      }
      
      clearCart();
      
      // Refresh orders
      await loadUserOrders(userEmail);
      
      return { id: orderId, ...finalOrderData };
    } catch (error) {
      console.error('Error placing order:', error);
      throw error;
    }
  };

  const loadUserOrders = useCallback(async (userEmail) => {
    try {
      console.log('Loading orders for user:', userEmail);
      const userOrders = await getUserOrders(userEmail);
      console.log('Loaded orders:', userOrders);
      setOrders(userOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  }, []);

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      orders,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      placeOrder,
      loadUserOrders,
      getTotal
    }}>
      {children}
    </CartContext.Provider>
  );
};
